import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ListingCard from '@/components/ListingCard';
import { ListingGridSkeleton } from '@/components/Skeletons';
import api, { cachedGet } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import SearchAutocomplete from '@/components/SearchAutocomplete';
import SEO from '@/components/SEO';
import { SlidersHorizontal, X, ChevronLeft, ChevronRight, BookOpen, ArrowRight } from 'lucide-react';

const CONDITIONS = ['neuf', 'occasion', 'reconditionne'];

const LISTINGS_BACKEND = process.env.REACT_APP_BACKEND_URL;
const blogImg = (src) => (!src ? null : (src.startsWith('http') ? src : `${LISTINGS_BACKEND}${src}`));
// Closest blog article per sector for the "À lire" box (shown only if published).
const SECTOR_ARTICLE = {
  piscine: 'fin-de-saison-piscine-stock',
  hvac: 'revendre-pompes-a-chaleur-climatiseurs-invendus',
};
const DEFAULT_SECTOR_ARTICLE = 'vendre-materiel-invendu-belgique';

// Per-sector SEO (title without the "| DestockPro" suffix — SEO adds it).
const ANNONCES_DESC = "Toutes les annonces de déstockage entre professionnels : matériel neuf, fins de série et surplus à prix réduits, en Belgique et pays voisins. Sans commission.";
const SECTOR_SEO = {
  piscine: {
    title: "Déstockage piscine entre professionnels",
    description: "Déstockage piscine entre professionnels : pompes, filtres, robots, pompes à chaleur, raccords et produits de traitement à prix réduits. Sans commission.",
  },
  hvac: {
    title: "Déstockage chauffage et climatisation",
    description: "Déstockage chauffage, ventilation et climatisation entre pros : pompes à chaleur, chaudières, climatiseurs et accessoires à prix réduits. Sans commission.",
  },
  plomberie: {
    title: "Déstockage plomberie et sanitaire",
    description: "Déstockage plomberie et sanitaire entre professionnels : robinetterie, chauffe-eau, raccords et tuyauterie à prix réduits, en Belgique. Sans commission.",
  },
  construction: {
    title: "Déstockage matériaux de construction",
    description: "Déstockage de matériaux de construction entre professionnels : surplus de chantier, fins de série et matériel neuf à prix réduits. Sans commission.",
  },
  electricite: {
    title: "Déstockage matériel électrique",
    description: "Déstockage de matériel électrique entre professionnels : câbles, tableaux, bornes de recharge et éclairage à prix réduits, en Belgique. Sans commission.",
  },
  jardinage: {
    title: "Déstockage jardin et espaces verts",
    description: "Déstockage jardin et espaces verts entre professionnels : machines, arrosage, clôtures et fournitures à prix réduits, en Belgique. Sans commission.",
  },
  outillage: {
    title: "Déstockage outillage professionnel",
    description: "Déstockage d'outillage professionnel entre pros : machines, outils électroportatifs et équipement d'atelier à prix réduits, en Belgique. Sans commission.",
  },
  autre: {
    title: "Autres déstockages entre professionnels",
    description: "Déstockage entre professionnels : matériel neuf, surplus et fins de série d'autres secteurs, à prix réduits, en Belgique et pays voisins. Sans commission.",
  },
};

const SORT_OPTIONS = [
  { value: 'newest', labelKey: 'sortNewest' },
  { value: 'oldest', labelKey: 'sortOldest' },
  { value: 'price_asc', labelKey: 'sortPriceAsc' },
  { value: 'price_desc', labelKey: 'sortPriceDesc' },
];

export default function ListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t, tc } = useTranslation('listings');
  const { t: tSector } = useTranslation('sectors');
  const { t: tCond } = useTranslation('conditions');
  const { t: tCat } = useTranslation('categories');

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sectorsData, setSectorsData] = useState({});
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [blogArticles, setBlogArticles] = useState([]);

  const query = searchParams.get('q') || '';
  const sector = searchParams.get('sector') || '';
  const category = searchParams.get('category') || '';
  const condition = searchParams.get('condition') || '';
  const seller = searchParams.get('seller') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const [searchInput, setSearchInput] = useState(query);

  const sectorKeys = Object.keys(sectorsData);
  const categories = sector && sectorsData[sector] ? sectorsData[sector].categories : [];

  useEffect(() => {
    cachedGet('/sectors').then(r => setSectorsData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/blog?limit=50').then(r => setBlogArticles(r.data.articles || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) api.get('/favorites/ids').then(r => setFavoriteIds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user]);

  const toggleFavorite = useCallback(async (listingId) => {
    if (!user) return;
    try {
      const { data } = await api.post(`/favorites/${listingId}`);
      setFavoriteIds(prev => data.favorited ? [...prev, listingId] : prev.filter(id => id !== listingId));
      toast.success(data.favorited ? tc('addedToFavorites') : tc('removedFromFavorites'));
    } catch {
      toast.error(tc('error'));
    }
  }, [user, tc]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (sector) params.set('sector', sector);
    if (category) params.set('category', category);
    if (condition) params.set('condition', condition);
    if (seller) params.set('seller', seller);
    if (sort) params.set('sort', sort);
    params.set('page', page.toString());
    api.get(`/listings/search?${params.toString()}`)
      .then((r) => {
        setListings(r.data.listings || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.pages || 1);
      })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [query, sector, category, condition, seller, sort, page]);

  // Load seller info when filtering by seller
  useEffect(() => {
    if (seller) {
      api.get(`/listings/seller/${seller}`).then(r => {
        setSellerInfo({ name: r.data.seller_name, company: r.data.company_name });
      }).catch(() => setSellerInfo(null));
    } else {
      setSellerInfo(null);
    }
  }, [seller]);

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (searchInput) p.set('q', searchInput); else p.delete('q');
    setSearchParams(p);
  };

  const setFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    if (key === 'sector') p.delete('category');
    p.delete('page');
    setSearchParams(p);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchInput('');
  };

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    if (p > 1) params.set('page', p.toString()); else params.delete('page');
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const hasFilters = query || sector || category;

  const sectorSeo = sector ? SECTOR_SEO[sector] : null;
  // A sector page with no listings should not be indexed while empty (follow so links still crawl).
  const emptySector = !!sector && !loading && listings.length === 0;

  // "À lire" box: closest article for the sector, shown only if it is published.
  const readAlsoSlug = sector ? (SECTOR_ARTICLE[sector] || DEFAULT_SECTOR_ARTICLE) : null;
  const readAlsoArticle = readAlsoSlug ? blogArticles.find((a) => a.slug === readAlsoSlug) : null;

  return (
    <div className="site-container py-8" data-testid="listings-page">
      <SEO
        title={sectorSeo ? sectorSeo.title : (query ? `Recherche "${query}"` : 'Toutes les annonces')}
        description={sectorSeo ? sectorSeo.description : (query ? `Résultats de recherche pour "${query}" sur DestockPro.` : ANNONCES_DESC)}
        path={`/annonces${sector ? '?sector=' + sector : ''}`}
        robots={emptySector ? 'noindex, follow' : undefined}
      />
      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-6" data-testid="listings-search-form">
        <SearchAutocomplete
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={handleSearch}
          placeholder={t('searchPlaceholder')}
        />
        <Button type="submit" className="bg-[#1F3F72] text-white" data-testid="listings-search-btn">
          {tc('search')}
        </Button>
        <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="listings-filter-toggle">
          <SlidersHorizontal className="w-4 h-4" />
        </Button>
      </form>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 p-4 border rounded-lg bg-neutral-50 space-y-4" data-testid="listings-filters">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 mb-1 block">{t('sector')}</label>
              <select
                className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white"
                value={sector}
                onChange={(e) => setFilter('sector', e.target.value)}
                data-testid="filter-sector"
              >
                <option value="">{t('allSectors')}</option>
                {sectorKeys.map((s) => <option key={s} value={s}>{tSector(s)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 mb-1 block">{t('category')}</label>
              <select
                className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white"
                value={category}
                onChange={(e) => setFilter('category', e.target.value)}
                disabled={!sector}
                data-testid="filter-category"
              >
                <option value="">{t('allCategories')}</option>
                {categories.map((c) => <option key={c} value={c}>{tCat(c)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 mb-1 block">{t('sortNewest').split(' ')[0]}</label>
              <select
                className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white"
                value={sort}
                onChange={(e) => setFilter('sort', e.target.value)}
                data-testid="filter-sort"
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-neutral-500" data-testid="clear-filters-btn">
              <X className="w-3 h-3 mr-1" /> {t('clearFilters')}
            </Button>
          )}
        </div>
      )}

      {/* Seller filter banner */}
      {seller && sellerInfo && (
        <div className="mb-6 flex items-center justify-between bg-[#1F3F72]/5 border border-[#1F3F72]/20 rounded-lg px-5 py-4" data-testid="seller-filter-banner">
          <div>
            <p className="text-sm text-neutral-500">{t('sellerListingsOf')}</p>
            <p className="font-heading text-lg font-bold text-[#1F3F72]">
              {sellerInfo.company || sellerInfo.name}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { const p = new URLSearchParams(searchParams); p.delete('seller'); setSearchParams(p); }}
            data-testid="clear-seller-filter"
          >
            <X className="w-3 h-3 mr-1" /> {t('viewAllListings')}
          </Button>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <ListingGridSkeleton count={8} />
      ) : listings.length === 0 ? (
        <div className="text-center py-20" data-testid="listings-empty">
          <p className="text-lg font-semibold text-neutral-700">{t('noResults')}</p>
          <p className="text-sm text-neutral-500 mt-1">{t('noResultsSub')}</p>
          {hasFilters && (
            <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>
              {t('clearFilters')}
            </Button>
          )}
        </div>
      ) : (
        <>
          <p className="text-sm text-neutral-500 mb-4" data-testid="listings-count">
            {total} {total === 1 ? t('result') : t('results')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 3xl:grid-cols-5 gap-6" data-testid="listings-grid">
            {listings.map((listing) => (
              <ListingCard key={listing.id} listing={listing} isFavorited={favoriteIds.includes(listing.id)} onToggleFavorite={user ? toggleFavorite : undefined} />
            ))}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`dots-${i}`} className="px-1 text-neutral-400 text-sm">...</span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === page ? 'default' : 'outline'}
                      size="sm"
                      className={p === page ? 'bg-[#1F3F72] text-white' : ''}
                      onClick={() => goToPage(p)}
                      data-testid={`pagination-page-${p}`}
                    >
                      {p}
                    </Button>
                  )
                )}
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
                data-testid="pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {readAlsoArticle && (
        <Link
          to={`/blog/${readAlsoArticle.slug}`}
          className="group mt-10 flex flex-col sm:flex-row items-stretch rounded-xl border border-neutral-200 bg-neutral-50 overflow-hidden hover:shadow-md transition-all"
          data-testid="listings-read-also"
        >
          {blogImg(readAlsoArticle.cover_image) && (
            <div className="sm:w-56 shrink-0 aspect-[16/9] sm:aspect-auto overflow-hidden bg-[#1F3F72]">
              <img src={blogImg(readAlsoArticle.cover_image)} alt={readAlsoArticle.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
            </div>
          )}
          <div className="p-5 sm:p-6 flex flex-col justify-center">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#F25C05] mb-2">
              <BookOpen className="w-3.5 h-3.5" /> {t('readAlso')}
            </span>
            <h3 className="font-heading text-lg font-bold text-neutral-900 leading-snug group-hover:text-[#1F3F72] transition-colors">{readAlsoArticle.title}</h3>
            {readAlsoArticle.excerpt && <p className="text-sm text-neutral-500 mt-1.5 line-clamp-2">{readAlsoArticle.excerpt}</p>}
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#1F3F72] mt-3">
              {t('readAlsoCta')} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </Link>
      )}
    </div>
  );
}
